import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import Foldout from 'components/shared/Foldout';
import { useTrackAIJob } from 'hooks/useTrackAIJob';
import { AIJobType } from 'models/AIJob';
import Admission from 'models/reviews/Admission';
import { admissionQueryKeys, admissionQueryParams } from 'services/api/admissions';
import { showReview } from 'services/api/reviews';
import { AIJobNotificationType, TrackedJob } from 'stores/aiJobNotificationStore';
import { colors } from 'styles/theme/colors';

const FOLDOUT_TRANSITION_MS = 500;

export default function AdmissionSlideout() {
  const { admissionId } = useParams<{ admissionId: string }>();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const { startTracking, isTracking } = useTrackAIJob();

  useEffect(() => {
    setIsOpen(true);
  }, []);

  const queryParams = admissionQueryParams.show(admissionId!);
  const { data: admission } = useQuery({
    queryKey: admissionQueryKeys.show(queryParams),
    queryFn: () => showReview(queryParams) as Promise<Admission>,
    enabled: !!admissionId,
  });

  useEffect(() => {
    if (!admission) return;

    const jobs = [
      admission.summaryJobForCurrentAttachments?.isProcessing
        ? { id: admission.summaryJobForCurrentAttachments.id, type: AIJobType.SUMMARY }
        : null,
      admission.criteriaEvaluationJob?.isProcessing
        ? { id: admission.criteriaEvaluationJob.id, type: AIJobType.CRITERIA_EVALUATION }
        : null,
    ].filter((j): j is TrackedJob => j !== null);

    if (
      jobs.length > 0 &&
      !isTracking({ jobbableId: admission.id, jobType: AIJobNotificationType.ADMISSION_SUMMARY })
    ) {
      startTracking({
        jobs,
        jobType: AIJobNotificationType.ADMISSION_SUMMARY,
        jobbableId: admission.id,
        returnUrl: `/admissions/${admission.id}/summary`,
      });
    }
  }, [admission, startTracking, isTracking]);

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => navigate('/admissions'), FOLDOUT_TRANSITION_MS);
  };

  return (
    <Foldout isNested open={isOpen} width='min(100%, 1200px)' onBackdropClick={handleClose}>
      <Container>
        <CloseIcon onClick={handleClose}>&times;</CloseIcon>
        <Placeholder>Needs implemented</Placeholder>
      </Container>
    </Foldout>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const CloseIcon = styled.div`
  color: ${colors.black75};
  cursor: pointer;
  font-size: 24px;
  position: absolute;
  right: 24px;
  top: 16px;
`;

const Placeholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${colors.black50};
  font-size: 14px;
`;
